import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Image,
  Box,
  Divider,
  Badge,
  Textarea,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconUser,
  IconCalendar,
  IconFileText,
  IconPhoto,
  IconFile,
  IconDownload,
} from "@tabler/icons-react";

function TaskApprovalDetailModal({
  approval,
  isOpen,
  onClose,
  onApprove,
  onReject,
}) {
  const [feedback, setFeedback] = useState("");
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Reset loading state and feedback whenever a new task is opened
  useEffect(() => {
    if (isOpen) {
      setIsApproving(false);
      setIsRejecting(false);
      setFeedback("");
    }
  }, [isOpen, approval?.completion_id]);

  if (!approval) return null;

  const handleApprove = () => {
    // Parent closes the modal and fires the API in the background — no async wait needed here
    onApprove(approval, feedback);
  };

  const handleReject = () => {
    // Parent closes the modal and fires the API in the background — no async wait needed here
    onReject(approval, feedback);
  };

  // Determine file type from URL
  const getFileType = url => {
    if (!url) return "unknown";
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) return "image";
    if (lowerUrl.match(/\.(mp4|mov|avi|webm)$/)) return "video";
    if (lowerUrl.match(/\.(pdf)$/)) return "pdf";
    return "file";
  };

  const renderFilePreview = file => {
    const fileType = getFileType(file.file_url);

    switch (fileType) {
      case "image":
        return (
          <Box key={file.id} style={{ position: "relative" }}>
            <Image
              src={file.file_url}
              alt="Submission"
              radius="md"
              fit="contain"
              style={{ maxHeight: 400, width: "100%", cursor: "pointer" }}
              onClick={() => window.open(file.file_url, "_blank")}
            />
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => window.open(file.file_url, "_blank")}
            >
              Open Full Size
            </Button>
          </Box>
        );

      case "video":
        return (
          <Box key={file.id}>
            <video
              controls
              style={{
                width: "100%",
                maxHeight: 400,
                borderRadius: 8,
                backgroundColor: "#000",
              }}
            >
              <source src={file.file_url} />
              Your browser does not support the video tag.
            </video>
          </Box>
        );

      case "pdf":
        return (
          <Paper key={file.id} p="md" withBorder radius="md">
            <Group>
              <ThemeIcon size="lg" variant="light" color="red">
                <IconFile size={24} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text fw={500}>PDF Document</Text>
                <Text size="sm" c="dimmed">
                  Click to view
                </Text>
              </Box>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => window.open(file.file_url, "_blank")}
              >
                Open PDF
              </Button>
            </Group>
          </Paper>
        );

      default:
        return (
          <Paper key={file.id} p="md" withBorder radius="md">
            <Group>
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconFile size={24} />
              </ThemeIcon>
              <Box style={{ flex: 1 }}>
                <Text fw={500}>Uploaded File</Text>
                <Text size="sm" c="dimmed">
                  Click to download
                </Text>
              </Box>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => window.open(file.file_url, "_blank")}
              >
                Download
              </Button>
            </Group>
          </Paper>
        );
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconFileText size={20} />
          <Text fw={600} size="lg">
            Task Submission Details
          </Text>
        </Group>
      }
      size="lg"
      padding="xl"
    >
      <Stack gap="lg">
        {/* Task Info */}
        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
            Task
          </Text>
          <Text fw={700} size="xl" mb="xs">
            {approval.task_name}
          </Text>
          {approval.task_desc && (
            <Text size="sm" c="dimmed">
              {approval.task_desc}
            </Text>
          )}
        </Box>

        <Divider />

        {/* Submission Info */}
        <Group gap="xl">
          <Box>
            <Group gap={6} mb={4}>
              <IconUser size={14} color="#868e96" />
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Submitted By
              </Text>
            </Group>
            <Text fw={600}>{approval.rep_name}</Text>
          </Box>

          <Box>
            <Group gap={6} mb={4}>
              <IconCalendar size={14} color="#868e96" />
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Submitted On
              </Text>
            </Group>
            <Text fw={600}>
              {new Date(approval.submitted_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </Text>
          </Box>

          <Box>
            <Group gap={6} mb={4}>
              <IconFileText size={14} color="#868e96" />
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Program
              </Text>
            </Group>
            <Badge variant="light" size="lg">
              {approval.program_name}
            </Badge>
          </Box>
        </Group>

        <Divider />

        {/* Text Response */}
        {approval.text_response && (
          <>
            <Box>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>
                Written Response
              </Text>
              <Paper p="md" withBorder radius="md" bg="gray.0">
                <Text style={{ whiteSpace: "pre-wrap" }}>
                  {approval.text_response}
                </Text>
              </Paper>
            </Box>
            <Divider />
          </>
        )}

        {/* Files/Images */}
        {approval.files && approval.files.length > 0 && (
          <>
            <Box>
              <Group gap={6} mb={8}>
                <IconPhoto size={16} color="#868e96" />
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Uploaded Files ({approval.files.length})
                </Text>
              </Group>
              <Stack gap="md">
                {approval.files.map(file => renderFilePreview(file))}
              </Stack>
            </Box>
            <Divider />
          </>
        )}

        {/* Feedback Section */}
        <Box>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>
            Feedback (Optional)
          </Text>
          <Textarea
            placeholder="Provide feedback to the rep about their submission..."
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            minRows={3}
            maxRows={6}
          />
        </Box>

        {/* Action Buttons */}
        <Group justify="space-between" gap="sm" pt="md">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isApproving || isRejecting}
          >
            Cancel
          </Button>
          <Group gap="sm">
            <Button
              color="red"
              variant="outline"
              leftSection={<IconX size={16} />}
              onClick={handleReject}
              loading={isRejecting}
              disabled={isApproving}
            >
              Reject
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              onClick={handleApprove}
              loading={isApproving}
              disabled={isRejecting}
            >
              Approve
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}

export default TaskApprovalDetailModal;
